/**
 * How a testing sheet is laid out, decided by arithmetic rather than by
 * whatever the browser's table algorithm feels like.
 *
 * Both failings this fixes were measured, not guessed. A twenty-four player
 * sheet of eight tests came out 250mm tall on a 210mm page — four names on a
 * second sheet nobody carries — and the columns were sized by their contents,
 * so six tests produced widths of 15.9mm and 32mm side by side. A test name in
 * a 15.9mm column wraps one word to a line, which is the "words get cut" a
 * coach sees.
 *
 * Mirrors `attendanceSheet.ts`, which answers the same question for the
 * register (D-039): the caller cannot ask for one page and be wrong about it,
 * because a sheet told to be exactly one page crops whatever does not fit.
 */

/** A4 less its margins, as `ReportView` sets them. */
const USABLE_WIDTH = { portrait: 210 - 2 * 14, landscape: 297 - 2 * 12 };

/** The shirt number. Two digits and a box. */
export const NUMBER_WIDTH = 8;

/**
 * The name column: 46mm holds "Mohammed Amine ELANZI" on one line at 3.2mm
 * type, and D-042 settled that a name a player cannot find himself in is not a
 * name. It gives up ten millimetres once the tests are many enough that the
 * measure columns are the thing under pressure — a surname still fits.
 */
const NAME_WIDE = 46;
const NAME_NARROW = 36;
const NAME_SQUEEZE_FROM = 7;

/**
 * What one character of a heading costs, as a multiple of the type size.
 *
 * Measured, not assumed. "Countermovement" was still overflowing its column
 * after the first attempt at this, at three different widths, and the three
 * measurements agreed: fifteen characters of IBM Plex Sans Semibold want 8.45
 * times the type size. The estimate they replaced was 7.8, which is how a word
 * ends up a millimetre wider than the box it is in.
 */
const CHAR_EMS = 0.563;

/** A heading cell's own padding, which is width the word does not get. */
const HEADER_PADDING = 1.2;

/** The longest word in the seeded catalogue: "Countermovement". */
export const DEFAULT_LONGEST_WORD = 15;

/** Legible on paper held at arm's length in daylight. Below this, split instead. */
const MIN_HEADER_FONT = 2.2;
const MAX_HEADER_FONT = 3.0;

/**
 * Comfortable rather than merely possible. Portrait gives up and turns the
 * long way round when it cannot hold the headings at this size.
 */
const COMFORTABLE_HEADER_FONT = 2.5;

/** The width a heading of this many characters needs at a given type size. */
function widthForWord(chars: number, font: number): number {
  return chars * CHAR_EMS * font + HEADER_PADDING;
}

/**
 * How many players one sheet holds at a height somebody can write a number in.
 *
 * Measured in the browser, like the register's figure: the rows divide whatever
 * is left after the club header, the column labels, the note and the footer,
 * and a row of 2.9mm type will not compress below about 4mm however little
 * padding it is given. Both numbers sit a couple of rows under what was
 * measured, because the measurement was taken in one font on one machine and a
 * sheet that overflows a page it was told to fit is a sheet with names missing
 * from the bottom.
 */
export const ROWS_PER_SHEET = { portrait: 44, landscape: 30 };

export interface SheetLayout {
  orientation: 'portrait' | 'landscape';
  numberWidth: string;
  nameWidth: string;
  /** Millimetres each test column gets. Equal, which is the point. */
  columnWidth: number;
  /** Header type size in millimetres, derived from the width it has to fit. */
  headerFont: number;
  /** Whether this many players can share one page without cropping. */
  onePage: boolean;
  /** Tests per sheet. Fewer than asked for means a second sheet, not smaller type. */
  perSheet: number;
}

function widthFor(orientation: 'portrait' | 'landscape', tests: number, name: number): number {
  return (USABLE_WIDTH[orientation] - NUMBER_WIDTH - name) / Math.max(tests, 1);
}

export function testingSheetLayout(
  testCount: number,
  playerCount: number,
  /**
   * The longest single word across the headings. A club's own test can be named
   * anything, and a sheet laid out for "Countermovement" does not fit
   * "Contre-mouvement" — so the caller measures its own words rather than
   * trusting the catalogue it shipped with.
   */
  longestWord: number = DEFAULT_LONGEST_WORD,
): SheetLayout {
  const name = testCount >= NAME_SQUEEZE_FROM ? NAME_NARROW : NAME_WIDE;

  // Portrait while it can hold the headings at a comfortable size; a sheet read
  // down a squad list is easier portrait, and the long way round costs ninety
  // millimetres of the height the rows live in.
  const portraitWidth = widthFor('portrait', testCount, name);
  const orientation =
    portraitWidth >= widthForWord(longestWord, COMFORTABLE_HEADER_FONT) ? 'portrait' : 'landscape';

  // Past what a landscape page can hold legibly, the sheet takes a second one
  // rather than shrinking the writing box — the register's answer to the same
  // question, and the reason `MAX_COLUMNS_PER_PAGE` exists there.
  const available = USABLE_WIDTH[orientation] - NUMBER_WIDTH - name;
  const perSheet = Math.max(
    1,
    Math.floor(available / widthForWord(longestWord, MIN_HEADER_FONT)),
  );
  const onSheet = Math.min(testCount, perSheet) || 1;
  const columnWidth = available / onSheet;

  return {
    orientation,
    numberWidth: `${NUMBER_WIDTH}mm`,
    nameWidth: `${name}mm`,
    columnWidth,
    headerFont: Math.min(
      MAX_HEADER_FONT,
      Math.max(MIN_HEADER_FONT, (columnWidth - HEADER_PADDING) / (longestWord * CHAR_EMS)),
    ),
    onePage: playerCount > 0 && playerCount <= ROWS_PER_SHEET[orientation] && testCount <= perSheet,
    perSheet,
  };
}

/** The longest unbroken word across a set of headings. */
export function longestWordIn(labels: readonly string[]): number {
  let longest = 1;
  for (const label of labels) {
    for (const word of label.split(/\s+/)) longest = Math.max(longest, word.length);
  }
  return longest;
}

/**
 * The tests of one sheet at a time, so a wide battery is two sheets rather than
 * small type.
 *
 * Balanced, not filled: fourteen tests at eleven a sheet is seven and seven,
 * never eleven and three. A second page carrying three columns stretched across
 * its width looks like a mistake, and the wider columns on both pages are the
 * point of splitting in the first place.
 */
export function chunkTests<T>(tests: readonly T[], perSheet: number): T[][] {
  if (tests.length <= perSheet) return [[...tests]];
  const sheets = Math.ceil(tests.length / perSheet);
  const per = Math.ceil(tests.length / sheets);
  const out: T[][] = [];
  for (let i = 0; i < tests.length; i += per) out.push(tests.slice(i, i + per));
  return out;
}
